import React, {useRef, useState} from 'react';

import {graphql, useStaticQuery} from 'gatsby';
import {Events} from 'src/analytics';
import {BoxProps, Form} from 'src/components/shared';
// Importing Input separately due to strange styled-components bug
import {Typography} from 'src/components/shared/typography';
import {useMarketo} from 'src/hooks/useMarketoForm';
import * as copy from 'src/utils/copy';
import {getMarketoFormElementId, marketoFormIds, munchkinId} from 'src/utils/marketo';
import styled from 'styled-components/macro';

import {SanityEmailSignUp} from 'types/generated';

const {P1} = Typography;

const SUBSCRIBE_ID = marketoFormIds.subscribe;
const SUBSCRIBE_FORM_ID = getMarketoFormElementId(SUBSCRIBE_ID);

export type EmailBannerProps = BoxProps &
  SanityEmailSignUp & {
    id: string;
    onSubmit?: VoidFunction;
    fullWidth: boolean;
    homepage: boolean;
  };

const HiddenMarketoForm = styled.form`
  display: none;
`;

export const EmailBanner: React.FC<EmailBannerProps> = ({
  id,
  onSubmit,
  Heading,
  subCopy,
  customizeEmailSignUp,
  fullWidth,
  homepage,
  ...props
}) => {
  const data = useStaticQuery(graphql`
    query EmailBannerQuery {
      sanityEmailSignup {
        Heading
        subCopy
      }
    }
  `);

  const emailInputId = `${id}-email-input`;
  const formRef = useRef<HTMLFormElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const [formKey, setFormKey] = useState(Date.now().toString());
  const [showSuccess, setShowSuccess] = useState(false);

  const onFormSubmit = () => {
    setFormKey(Date.now().toString());
    setShowSuccess(true);
    formRef?.current?.reset();
    onSubmit?.();
    return false;
  };

  useMarketo({
    munchkinId,
    formId: SUBSCRIBE_ID,
    formKey,
    callback: form => {
      form.onSuccess(onFormSubmit);
    },
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    setShowSuccess(false);
    Events.didSubmitForm({page: window?.document?.title, form: 'Newsletter Email Form'});
    const form = window?.MktoForms2?.getForm(SUBSCRIBE_ID);
    form?.setValues({Email: emailRef?.current?.value || ''});
    form?.submit();

    return false;
  };
  const temporary_shouldHideNewsletterSignup = true;

  if (temporary_shouldHideNewsletterSignup) {
    return null;
  }
  return (
    <section
      className={`px-gutter bg-beige text-black py-8 no-prose ${fullWidth != true ? ' mb-11 md:mb-10' : ''}`}
      data-module="banner-newsletter"
    >
      <div className="wrapper-default flex flex-wrap md:flex-nowrap lg:gap-8 items-center justify-between">
        <div className={`w-full type-epsilon md:pr-4 ${homepage == true ? 'md:w-3/4' : ' md:w-1/2 '}`}>
          {customizeEmailSignUp == false ? data.sanityEmailSignup?.Heading : Heading}
        </div>
        <HiddenMarketoForm id={SUBSCRIBE_FORM_ID} />
        <div className="mt-4 lg:mt-0 lg:flex w-full md:w-auto flex-col">
          {!fullWidth && (
            <span className=" type-sm-p mb-2 block">
              {customizeEmailSignUp == false ? data.sanityEmailSignup?.subCopy : subCopy}
            </span>
          )}

          <Form
            ref={formRef}
            id={id}
            onSubmit={handleSubmit}
            className="bg-white text-green-dark rounded-full py-3 px-5 flex justify-between max-w-[320px] md:w-[340px] lg:w-[350px] w-full  newsletter-wrapper"
          >
            <input
              ref={emailRef}
              id={emailInputId}
              name="email"
              type="email"
              placeholder={'email address'}
              className="type-sm-p focus:outline-0 p-0 border-none bg-transparent"
            />
            <button
              type="submit"
              className="type-eyebrow text-green-dark hover:text-black hover:underline hover:decoration-lighter-gray"
            >
              SUBMIT
            </button>
            {showSuccess && (
              <P1 color="lightGreenAriaText" mt="12px">
                {copy.newsletterForm.successMessage}
              </P1>
            )}
          </Form>
        </div>
      </div>
    </section>
  );
};
